/**
 * Education product cart rules (AU). Config: window.yr.education from theme settings.
 */
class EducationCartRules {
  static get config() {
    return window.yr?.education || {};
  }

  static get productId() {
    const id = this.config.productId;
    return id ? String(id).trim() : '';
  }

  static isEnabled() {
    return Boolean(this.productId);
  }

  static isEducationProduct(productId) {
    return String(productId) === this.productId;
  }

  static isEducationLineItem(item) {
    return item && this.isEducationProduct(item.product_id);
  }

  static cartHasEducation(cart) {
    return cart?.items?.some((item) => this.isEducationLineItem(item)) ?? false;
  }

  static cartHasPhysical(cart) {
    return (
      cart?.items?.some(
        (item) => item.requires_shipping === true && !this.isEducationLineItem(item)
      ) ?? false
    );
  }

  static get messages() {
    return this.config.messages || {};
  }

  static async fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`);
    if (!response.ok) throw new Error('Failed to load cart');
    return response.json();
  }

  /**
   * @param {object} cart
   * @param {{ productId: string, variantId: string, quantity: number, requiresShipping: boolean }} payload
   */
  static validateAddToCart(cart, { productId, variantId, quantity, requiresShipping }) {
    if (!this.isEnabled()) return { ok: true };

    const qty = Number(quantity) || 1;
    const isEducation = this.isEducationProduct(productId);
    const isPhysical = requiresShipping === true;

    if (isEducation) {
      const educationLines = cart.items.filter((item) => this.isEducationLineItem(item));

      if (educationLines.length > 0) {
        const existing = educationLines[0];
        if (String(existing.variant_id) !== String(variantId)) {
          return { ok: false, message: this.messages.maxVariant };
        }
        if (existing.quantity + qty > 1) {
          return { ok: false, message: this.messages.maxQuantity };
        }
      } else if (qty > 1) {
        return { ok: false, message: this.messages.maxQuantity };
      }

      if (this.cartHasPhysical(cart)) {
        return { ok: false, message: this.messages.educationWithPhysicalInCart };
      }
    } else if (isPhysical && this.cartHasEducation(cart)) {
      return { ok: false, message: this.messages.physicalWithEducationInCart };
    }

    return { ok: true };
  }

  /**
   * @param {object} cart
   * @param {{ lineItem: object, quantity: number }} payload
   */
  static validateQuantityChange(cart, { lineItem, quantity }) {
    if (!this.isEnabled() || !lineItem) return { ok: true };

    const qty = Number(quantity);
    if (this.isEducationLineItem(lineItem) && qty > 1) {
      return { ok: false, message: this.messages.maxQuantity };
    }

    return { ok: true };
  }
}

window.EducationCartRules = EducationCartRules;
